import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useTabs } from "@/contexts/tabs-context";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";

type InAppChannelLink = {
	workspaceId: string;
	channelId: string;
};

const CHANNEL_SECTIONS = new Set([
	"chats",
	"docs",
	"boards",
	"meets",
	"sheets",
	"storage",
	"passwords",
	"automations",
]);

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

const digitRegex = /^\d+$/;

const isInternalHost = (hostname: string, currentHost?: string) => {
	const normalized = hostname.toLowerCase();
	const normalizedCurrent = currentHost?.toLowerCase();

	if (normalized !== normalizedCurrent) return false;
 
	if (normalizedCurrent && normalized === normalizedCurrent) return true;

	const currentIsLocal = normalizedCurrent
		? LOCAL_HOSTS.has(normalizedCurrent)
		: false;
	if (currentIsLocal && LOCAL_HOSTS.has(normalized)) return true;

	return normalized === "tabbed.io" || normalized.endsWith(".tabbed.io");
};

const parseInAppChannelLink = (href: string): InAppChannelLink | null => {
	if (!href || typeof window === "undefined") return null;

	let url: URL;
	try {
		url = new URL(href, window.location.origin);
	} catch {
		return null;
	}

	if (!isInternalHost(url.hostname, window.location.hostname)) return null;

	const parts = url.pathname.split("/").filter(Boolean);
	if (parts.length < 3) return null;

	const [workspaceId, section, channelIdCandidate] = parts;

	if (!digitRegex.test(workspaceId)) return null;

	if (!CHANNEL_SECTIONS.has(section)) return null;
	
	if (!channelIdCandidate || !digitRegex.test(channelIdCandidate)) return null;

	return { workspaceId, channelId: channelIdCandidate };
};

export function useChannelLinkHandler() {
	const { currentWorkspace } = WorkspaceRoute.useLoaderData();
	const { channels, channelsLoading, userWorkspaces } = useWorkspaceContext();
	const { openTemporaryTab, openTabInBackground, createTabDataFromChannel, tabs, activeTabId } = useTabs();

	const activePanelId = useMemo(
		() => tabs.find((tab) => tab.id === activeTabId)?.panelId,
		[tabs, activeTabId],
	);

	const openLinkInApp = useCallback(
		(href: string, openInBackground = false) => {
			const parsed = parseInAppChannelLink(href);
			if (!parsed) return false;

			const targetWorkspaceId = Number(parsed.workspaceId);
			if (Number(currentWorkspace.id) !== targetWorkspaceId) {
				const hasAccess = userWorkspaces?.some(
					(workspace) => workspace.id === targetWorkspaceId,
				);
				toast(
					hasAccess
						? "Switch to that workspace to open this channel."
						: "You do not have access to that workspace.",
				);
				return true;
			}

			if (channelsLoading) {
				toast("Loading channels. Try again in a moment.");
				return true;
			}

			const channel = channels?.find(
				(item) => item.id === Number(parsed.channelId),
			);
			if (!channel) {
				toast("You are not a member of that channel.");
				return true;
			}

			const tabData = createTabDataFromChannel(currentWorkspace.id, channel);

			if (openInBackground) {
				openTabInBackground(tabData, activePanelId ?? "default");
			} else {
				openTemporaryTab(tabData, activePanelId ?? "default");
			}

			return true;
		},
		[
			currentWorkspace.id,
			userWorkspaces,
			channelsLoading,
			channels,
			createTabDataFromChannel,
			openTemporaryTab,
			openTabInBackground,
			activePanelId,
		],
	);

	const handleLinkClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
			// Check for Cmd (Mac) or Ctrl (Windows/Linux) key
			const shouldOpenInBackground = event.metaKey || event.ctrlKey;

			if (openLinkInApp(href, shouldOpenInBackground)) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		[openLinkInApp],
	);

	return { handleLinkClick, openLinkInApp };
}
